/**
 * Reminder Composer — Sprint P0 (17 Jul 2026) · UX-hardening (18 Jul 2026)
 *                    · Redesign+Stripe Sprint (18 Feb 2026).
 *
 * REGEL 2 (single source): alle content, statussen en bedragen komen uit
 * backend endpoints. Geen client-side sommaties, geen lokale statuslogica.
 *
 * REGEL 1 (tenant-onafhankelijk): alle labels in NL/EN/ES. Bank- en
 * bedrijfsgegevens uit /admin/settings/company-payment-details.
 *
 * Redesign+Stripe (18 Feb 2026):
 *  - Nieuwe reminder-mail template (grote titel + bedragblok + CTA "Betaal direct")
 *  - Stripe-betaallink: knop "Betaallink aanmaken" per factuur
 *  - Bijlagen sectie prominenter (vinkjes, klikbaar)
 *  - Uitgebreide reminder-historie (Resend ID, Stripe status, ontvanger)
 *  - Verzendbevestiging als professioneel resultaatvenster (i.p.v. toast)
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle, CheckCircle2, Info, Loader2, ExternalLink, Copy,
  Mail, Paperclip, RefreshCw, Send, X, Beaker, FileText, CreditCard,
  ChevronDown, ChevronUp, Zap, History,
} from "lucide-react";
import { adminApi, formatApiError } from "../../../context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { Label } from "../../ui/label";
import { Badge } from "../../ui/badge";
import { Checkbox } from "../../ui/checkbox";
import { Alert, AlertDescription } from "../../ui/alert";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../ui/select";

const LANGUAGES = [
  { value: "nl", label: "Nederlands" },
  { value: "en", label: "English" },
  { value: "es", label: "Español" },
];

const REMINDER_ELIGIBLE = new Set(["verzonden", "open", "deels_betaald"]);

// Placeholders met korte NL-uitleg voor quick-insert
const PLACEHOLDERS = [
  { token: "{customer_name}", label: "Klantnaam" },
  { token: "{invoice_number}", label: "Factuurnr" },
  { token: "{amount_due}", label: "Openstaand" },
  { token: "{currency}", label: "Valuta" },
  { token: "{due_date}", label: "Vervaldatum" },
  { token: "{days_overdue}", label: "Dagen te laat" },
  { token: "{company_name}", label: "Uw bedrijf" },
  { token: "{invoice_lines}", label: "Facturen-lijst" },
];

// Regex om `[Uw eigen bericht ...]` / EN / ES te herkennen
const CUSTOM_TEMPLATE_PATTERNS = [
  /\[uw eigen bericht[^\]]*\]/i,
  /\[your custom message[^\]]*\]/i,
  /\[su mensaje personalizado[^\]]*\]/i,
];

function fmtMoney(x, cur = "EUR") {
  const n = Number(x || 0);
  return `${cur} ${n.toLocaleString("nl-NL", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
}

function dedupeEmails(list) {
  const seen = new Set();
  return (list || [])
    .map((s) => (s || "").trim())
    .filter((s) => s && s.includes("@") && !seen.has(s.toLowerCase()) && seen.add(s.toLowerCase()));
}

function hasUnfilledCustomPlaceholder(text) {
  const t = (text || "").toLowerCase();
  return CUSTOM_TEMPLATE_PATTERNS.some((rx) => rx.test(t));
}

export default function ReminderComposer({
  open,
  onOpenChange,
  customerId,
  focusInvoiceId = null,
  currentUserEmail = "",
  onSent,
}) {
  // ─── State ─────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [customer, setCustomer] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [templates, setTemplates] = useState([]);
  const [language, setLanguage] = useState("nl");
  const [reminderType, setReminderType] = useState("first_reminder");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [bodyDirty, setBodyDirty] = useState(false);  // markeert user-edits

  // Ontvangers
  const [toInput, setToInput] = useState("");
  const [ccInput, setCcInput] = useState("");
  const [bccInput, setBccInput] = useState("");
  const [showCcBcc, setShowCcBcc] = useState(false);

  // Bijlagen + betaalinfo opties
  const [attachInvoicePdfs, setAttachInvoicePdfs] = useState(true);
  const [attachOutstandingOverview, setAttachOutstandingOverview] = useState(false);
  const [showBank, setShowBank] = useState(true);
  const [showPaymentReference, setShowPaymentReference] = useState(true);
  const [showPaymentLink, setShowPaymentLink] = useState(false);
  const [showPaymentInstructions, setShowPaymentInstructions] = useState(true);

  // Preview + historie + acties
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  // Stripe betaallink management (Sprint 18 Feb 2026)
  const [stripeSession, setStripeSession] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  // Bundle Stripe checkout — één betaallink voor meerdere facturen
  const [bundleSession, setBundleSession] = useState(null);
  const [bundleLoading, setBundleLoading] = useState(false);

  // Verzendbevestiging resultaatvenster (P0.7)
  const [sendResult, setSendResult] = useState(null);

  const bodyRef = useRef(null);

  // ─── Load data on open ─────────────────────────────────────────
  const loadInitial = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const [cust, tpls] = await Promise.all([
        adminApi.get(`/admin/customers/${customerId}/outstanding-invoices`),
        adminApi.get(`/admin/reminders/composer/templates?language=${language}`),
      ]);
      setCustomer({
        id: cust.data.customer_id,
        name: cust.data.customer_name,
        email: cust.data.customer_email,
      });
      setInvoices(cust.data.invoices || []);
      setTemplates(tpls.data.types || []);

      const eligibleIds = (cust.data.invoices || [])
        .filter((i) => REMINDER_ELIGIBLE.has(i.financial_status))
        .map((i) => i.id);
      if (focusInvoiceId && eligibleIds.includes(focusInvoiceId)) {
        setSelectedIds(new Set([focusInvoiceId]));
      } else if (eligibleIds.length === 1) {
        setSelectedIds(new Set(eligibleIds));
      } else if (focusInvoiceId) {
        setSelectedIds(new Set([focusInvoiceId]));
      } else {
        setSelectedIds(new Set());
      }

      const initial = (tpls.data.types || []).find((t) => t.value === "first_reminder")
        || (tpls.data.types || [])[0];
      if (initial) {
        setSubject(initial.subject || "");
        setBody(initial.body || "");
        setBodyDirty(false);
      }
      if (cust.data.customer_email) {
        setToInput(cust.data.customer_email);
      }
      // Reset UI flags
      setShowCcBcc(false);
      setShowHistory(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [customerId, focusInvoiceId, language]);

  useEffect(() => {
    if (open) loadInitial();
  }, [open, loadInitial]);

  // ─── Reload templates when language changes ─────────────────────
  const reloadTemplatesForLang = useCallback(async (lang, type, force = false) => {
    try {
      const r = await adminApi.get(`/admin/reminders/composer/templates?language=${lang}`);
      setTemplates(r.data.types || []);
      const chosen = (r.data.types || []).find((t) => t.value === type);
      if (chosen && (force || !bodyDirty)) {
        setSubject(chosen.subject || "");
        setBody(chosen.body || "");
        setBodyDirty(false);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [bodyDirty]);

  // ─── Load history voor eerste geselecteerde factuur ─────────────
  useEffect(() => {
    const firstId = [...selectedIds][0];
    if (!open || !firstId) {
      setHistory([]);
      return;
    }
    (async () => {
      try {
        const r = await adminApi.get(
          `/admin/reminders/composer/history/${firstId}?limit=20`,
        );
        setHistory(r.data?.items || []);
      } catch {
        setHistory([]);
      }
    })();
  }, [open, selectedIds]);

  // ─── Preview (debounced) ────────────────────────────────────────
  const buildPayload = useCallback(() => ({
    invoice_ids: [...selectedIds],
    subject,
    body,
    language,
    reminder_type: reminderType,
    show_bank: showBank,
    show_payment_reference: showPaymentReference,
    show_payment_link: showPaymentLink,
    show_payment_instructions: showPaymentInstructions,
    attach_invoice_pdfs: attachInvoicePdfs,
    attach_outstanding_overview: attachOutstandingOverview,
    to_addresses: dedupeEmails(toInput.split(/[,;]/)),
    cc_addresses: dedupeEmails(ccInput.split(/[,;]/)),
    bcc_addresses: dedupeEmails(bccInput.split(/[,;]/)),
  }), [
    selectedIds, subject, body, language, reminderType,
    showBank, showPaymentReference, showPaymentLink, showPaymentInstructions,
    attachInvoicePdfs, attachOutstandingOverview,
    toInput, ccInput, bccInput,
  ]);

  useEffect(() => {
    if (!open) return;
    if (selectedIds.size === 0) {
      setPreview(null);
      return;
    }
    const handle = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const r = await adminApi.post(
          "/admin/reminders/composer/preview", buildPayload(),
        );
        setPreview(r.data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setPreviewLoading(false);
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [open, buildPayload, selectedIds]);

  // ─── Derived ────────────────────────────────────────────────────
  const selectedInvoices = useMemo(
    () => invoices.filter((i) => selectedIds.has(i.id)),
    [invoices, selectedIds],
  );

  // Server-side total_due is de canonical bron (Regel 2). Fallback: som.
  const selectedTotalDue = useMemo(() => {
    if (preview?.total_due !== undefined && preview?.total_due !== null) {
      return Number(preview.total_due) || 0;
    }
    return selectedInvoices.reduce(
      (s, i) => s + Number(i.amount_due || 0), 0,
    );
  }, [preview, selectedInvoices]);

  const currency = useMemo(() => (
    preview?.currency || selectedInvoices[0]?.currency || "EUR"
  ), [preview, selectedInvoices]);

  const toList = useMemo(() => dedupeEmails(toInput.split(/[,;]/)), [toInput]);
  const ccList = useMemo(() => dedupeEmails(ccInput.split(/[,;]/)), [ccInput]);
  const bccList = useMemo(() => dedupeEmails(bccInput.split(/[,;]/)), [bccInput]);

  const remindersSentToday = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return history.filter(
      (h) => h.status === "sent" && (h.sent_at || "").slice(0, 10) === today,
    ).length;
  }, [history]);

  const hasUnfilled = useMemo(() => hasUnfilledCustomPlaceholder(body), [body]);
  const emptyBody = useMemo(() => (body || "").trim().length === 0, [body]);

  const clientWarnings = useMemo(() => {
    const w = [];
    if (selectedIds.size === 0) w.push("Geen facturen geselecteerd.");
    if (selectedTotalDue <= 0 && selectedIds.size > 0) {
      w.push("Totaal geselecteerd openstaand bedrag is nul — controleer of de betaling reeds is geregistreerd.");
    }
    if (toList.length === 0) w.push("Geen geldig e-mailadres in Aan-veld.");
    if (remindersSentToday > 0) {
      w.push(`Vandaag is al ${remindersSentToday} herinnering(en) verstuurd voor deze factuur.`);
    }
    if (hasUnfilled) {
      w.push("Berichttekst bevat nog een [...]-placeholder. Vervang deze voor u verzendt.");
    }
    if (emptyBody) w.push("Berichttekst is leeg.");
    return w;
  }, [selectedIds, selectedTotalDue, toList, remindersSentToday, hasUnfilled, emptyBody]);

  // Non-blocking warnings from preview (dedupe met client-side)
  const combinedWarnings = useMemo(() => {
    const list = [...clientWarnings];
    const seen = new Set(list.map((w) => (w || "").toLowerCase()));
    (preview?.warnings || []).forEach((w) => {
      const lc = (w || "").toLowerCase();
      // Dedupe op inhoudelijke overlap voor placeholder-warning
      const isPlaceholder = lc.includes("[...]") || lc.includes("placeholder");
      const alreadyPlaceholder = list.some((existing) => {
        const e = existing.toLowerCase();
        return e.includes("[...]") || e.includes("placeholder");
      });
      if (isPlaceholder && alreadyPlaceholder) return;
      if (!seen.has(lc)) {
        seen.add(lc);
        list.push(w);
      }
    });
    return list;
  }, [clientWarnings, preview]);

  const sendBlocked = useMemo(() => (
    selectedIds.size === 0 || toList.length === 0
    || hasUnfilled || emptyBody
    || selectedInvoices.some((i) => !REMINDER_ELIGIBLE.has(i.financial_status))
  ), [selectedIds, toList, hasUnfilled, emptyBody, selectedInvoices]);

  // ─── Actions ────────────────────────────────────────────────────
  const toggleInvoice = (id) => {
    const s = new Set(selectedIds);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelectedIds(s);
  };

  const handleTypeChange = (v) => {
    // Wissel type — als body dirty is: vraag bevestiging
    if (bodyDirty) {
      const ok = window.confirm(
        "U heeft de berichttekst aangepast. Wilt u deze aanpassingen "
        + "verwerpen en het gekozen template opnieuw laden?",
      );
      if (!ok) return;
    }
    setReminderType(v);
    const chosen = templates.find((t) => t.value === v);
    if (chosen) {
      setSubject(chosen.subject || "");
      setBody(chosen.body || "");
      setBodyDirty(false);
    }
  };

  const handleLangChange = (v) => {
    if (bodyDirty) {
      const ok = window.confirm(
        "U heeft de berichttekst aangepast. Wisselen van taal laadt "
        + "opnieuw het templatetekst. Doorgaan?",
      );
      if (!ok) return;
    }
    setLanguage(v);
    reloadTemplatesForLang(v, reminderType, true);
  };

  const insertPlaceholder = (token) => {
    // Insert at cursor in body textarea
    const el = bodyRef.current;
    if (!el) {
      setBody((b) => b + token);
      setBodyDirty(true);
      return;
    }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    const next = body.slice(0, start) + token + body.slice(end);
    setBody(next);
    setBodyDirty(true);
    // Move caret na de placeholder
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const submitSend = async (testMode) => {
    if (selectedIds.size === 0) {
      toast.error("Selecteer minimaal één factuur.");
      return;
    }
    setSending(true);
    try {
      const payload = { ...buildPayload(), test_mode: testMode };
      if (testMode) {
        payload.to_addresses = currentUserEmail
          ? [currentUserEmail] : payload.to_addresses;
        payload.cc_addresses = [];
        payload.bcc_addresses = [];
      }
      const r = await adminApi.post("/admin/reminders/composer/send", payload);
      const st = r.data?.status;
      if (st === "sent") {
        if (testMode) {
          toast.success(`Testmail verzonden naar ${r.data?.to}`);
        } else {
          // P0.7: professioneel resultaatvenster i.p.v. simpele toast
          setSendResult(r.data);
          setConfirmOpen(false);
        }
      } else if (st === "skipped_no_key") {
        // Preview omgeving zonder Resend key: toch resultaatvenster voor UX
        if (!testMode) {
          setSendResult({
            ...(r.data || {}),
            status: "skipped_no_key",
            message: "E-mail overgeslagen: Resend API-sleutel ontbreekt (preview).",
          });
          setConfirmOpen(false);
        } else {
          toast.warning("Testmail overgeslagen: Resend-key ontbreekt (preview).");
        }
      } else {
        toast.warning(`Status: ${st || "onbekend"}`);
      }
      if (!testMode && st === "sent") {
        onSent?.(r.data);
        // NIET onOpenChange(false) — laat resultaatvenster tonen
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
      setConfirmOpen(false);
    }
  };

  // ─── Stripe betaallink management ───────────────────────────────
  const loadStripeSession = useCallback(async (invoiceId) => {
    if (!invoiceId) { setStripeSession(null); return; }
    try {
      const r = await adminApi.get(
        `/admin/sales-invoices/${invoiceId}/stripe-session`,
      );
      setStripeSession(r.data?.session || null);
    } catch {
      setStripeSession(null);
    }
  }, []);

  const createStripeLink = async () => {
    const firstId = [...selectedIds][0];
    if (!firstId) {
      toast.error("Selecteer eerst een factuur.");
      return;
    }
    setStripeLoading(true);
    try {
      const r = await adminApi.post(
        `/admin/sales-invoices/${firstId}/stripe-checkout`,
        {
          invoice_id: firstId,
          origin_url: window.location.origin,
          customer_type: "business", country: "NL", payment_method: "card",
          language,
        },
      );
      setStripeSession(r.data);
      toast.success("Stripe-betaallink aangemaakt. Preview bijgewerkt met CTA-knop.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setStripeLoading(false);
    }
  };

  const expireStripeLink = async () => {
    const firstId = [...selectedIds][0];
    if (!firstId) return;
    if (!window.confirm("Weet u zeker dat u de actieve betaallink wilt intrekken?")) return;
    try {
      await adminApi.post(
        `/admin/sales-invoices/${firstId}/stripe-checkout/expire`,
      );
      setStripeSession(null);
      toast.success("Betaallink ingetrokken.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Auto-load Stripe session when invoice selection changes
  useEffect(() => {
    const firstId = [...selectedIds][0];
    if (open && firstId && selectedIds.size === 1) {
      loadStripeSession(firstId);
      setBundleSession(null);
    } else if (open && selectedIds.size > 1) {
      setStripeSession(null);
      // Check for existing bundle session
      const ids = [...selectedIds];
      (async () => {
        try {
          const r = await adminApi.get("/admin/invoice-bundles/stripe-session", {
            params: { invoice_ids: ids.join(",") },
          });
          setBundleSession(r.data?.session || null);
        } catch {
          setBundleSession(null);
        }
      })();
    } else {
      setStripeSession(null);
      setBundleSession(null);
    }
  }, [open, selectedIds, loadStripeSession]);

  const createBundleStripeLink = async () => {
    const ids = [...selectedIds];
    if (ids.length < 2) {
      toast.error("Selecteer minimaal 2 facturen voor een gebundelde betaallink.");
      return;
    }
    setBundleLoading(true);
    try {
      const r = await adminApi.post("/admin/invoice-bundles/stripe-checkout", {
        invoice_ids: ids,
        origin_url: window.location.origin,
        customer_type: "business",
        country: "NL",
        payment_method: "card",
        language,
      });
      setBundleSession(r.data);
      toast.success(
        `Gebundelde betaallink aangemaakt voor ${ids.length} facturen · totaal ${r.data.currency} ${Number(r.data.total_charge || 0).toFixed(2)}`,
      );
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBundleLoading(false);
    }
  };

  // Sneltoetsen: Ctrl+Enter = openen confirm, Esc handled via Radix
  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
      if (!sendBlocked) {
        setConfirmOpen(true);
      }
    }
  };

  // ─── Render ─────────────────────────────────────────────────────
  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[1400px] w-[95vw] max-h-[95vh] flex flex-col p-0 overflow-hidden rounded-md"
        data-testid="reminder-composer"
        onKeyDown={handleKeyDown}
      >
        <DialogHeader className="px-6 py-3 border-b border-slate-200 bg-slate-50">
          <DialogTitle className="flex items-center gap-2 text-[#0A192F]">
            <Mail className="w-5 h-5" />
            Reminder Composer
            {customer?.name && (
              <span className="text-sm font-normal text-slate-500">
                — {customer.name}
              </span>
            )}
            {remindersSentToday > 0 && (
              <Badge
                className="ml-2 bg-amber-100 text-amber-900 border-amber-300"
                data-testid="composer-sent-today-badge"
              >
                <History className="w-3 h-3 mr-1" />
                {remindersSentToday} vandaag verstuurd
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Stel een betalingsherinnering samen — kies facturen, taal en template, bewerk onderwerp en tekst, en verzend via e-mail.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex-1 flex items-center justify-center p-10">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : (
          <>
            {/* Header met saldo */}
            <div
              className="grid grid-cols-2 lg:grid-cols-5 gap-3 px-6 py-3 bg-white border-b border-slate-200"
              data-testid="composer-header-summary"
            >
              <div>
                <div className="text-[11px] uppercase text-slate-500">Klant</div>
                <div className="font-medium text-slate-900 truncate" data-testid="composer-customer-name">
                  {customer?.name || "—"}
                </div>
                <div className="text-xs text-slate-500 truncate">{customer?.email || "geen e-mail"}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase text-slate-500">Geselecteerd</div>
                <div className="font-medium text-slate-900" data-testid="composer-selected-count">
                  {selectedIds.size} factuur{selectedIds.size !== 1 ? "en" : ""}
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase text-slate-500">Totaal openstaand</div>
                <div className="font-semibold text-slate-900" data-testid="composer-selected-total">
                  {fmtMoney(selectedTotalDue, currency)}
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase text-slate-500">Type</div>
                <div className="font-medium text-slate-900 text-sm truncate">
                  {templates.find((t) => t.value === reminderType)?.label || reminderType}
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase text-slate-500">Taal</div>
                <div className="font-medium text-slate-900 text-sm">
                  {LANGUAGES.find((l) => l.value === language)?.label}
                </div>
              </div>
            </div>

            {/* Main 3-column grid */}
            <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-4 p-4 overflow-hidden min-h-0">
              {/* ── Links: factuurselectie + instellingen ─────────────── */}
              <div className="lg:col-span-4 flex flex-col gap-3 overflow-y-auto min-h-0 pr-2">
                <SectionTitle>Facturen ({invoices.length})</SectionTitle>
                <div className="border border-slate-200 rounded" data-testid="composer-invoices-list">
                  {invoices.length === 0 && (
                    <div className="p-3 text-sm text-slate-500 italic">
                      Geen facturen voor deze klant.
                    </div>
                  )}
                  {invoices.map((inv) => {
                    const eligible = REMINDER_ELIGIBLE.has(inv.financial_status);
                    const isSel = selectedIds.has(inv.id);
                    return (
                      <div
                        key={inv.id}
                        className={`flex items-start gap-2 p-2 border-b border-slate-100 last:border-b-0 ${
                          !eligible ? "bg-slate-50 opacity-70" : isSel ? "bg-emerald-50/40" : ""
                        }`}
                        data-testid={`composer-invoice-row-${inv.invoice_number}`}
                      >
                        <Checkbox
                          checked={isSel}
                          onCheckedChange={() => eligible && toggleInvoice(inv.id)}
                          disabled={!eligible}
                          className="mt-1"
                          data-testid={`composer-invoice-checkbox-${inv.invoice_number}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm truncate">{inv.invoice_number}</span>
                            <FinancialStatusBadge status={inv.financial_status} />
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            {inv.invoice_date} · vervalt {inv.due_date || "—"}
                          </div>
                          <div className="text-xs mt-1 flex flex-wrap gap-x-3">
                            <span>Totaal: {fmtMoney(inv.total_incl_vat, inv.currency)}</span>
                            {Number(inv.total_paid) > 0 && (
                              <span>Betaald: {fmtMoney(inv.total_paid, inv.currency)}</span>
                            )}
                            <span className="font-semibold text-slate-900">
                              Open: {fmtMoney(inv.amount_due, inv.currency)}
                            </span>
                          </div>
                          {inv.previous_reminder_count > 0 && (
                            <div className="text-[11px] text-amber-700 mt-0.5 flex items-center gap-1">
                              <History className="w-3 h-3" />
                              {inv.previous_reminder_count} eerdere herinnering{inv.previous_reminder_count > 1 ? "en" : ""}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <SectionTitle>Instellingen</SectionTitle>
                <div className="space-y-3 border border-slate-200 rounded p-3">
                  <div>
                    <Label className="text-xs">Herinneringstype</Label>
                    <Select value={reminderType} onValueChange={handleTypeChange}>
                      <SelectTrigger className="mt-1" data-testid="composer-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {templates.map((t) => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {reminderType === "custom" && (
                      <div className="mt-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                        Vervang de placeholder <span className="font-mono">[Uw eigen bericht hier...]</span> door uw eigen tekst.
                      </div>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs">Taal</Label>
                    <Select value={language} onValueChange={handleLangChange}>
                      <SelectTrigger className="mt-1" data-testid="composer-language-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {LANGUAGES.map((l) => (
                          <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="pt-2 border-t border-slate-100">
                    <div className="text-xs font-semibold uppercase text-slate-500 mb-2">Bijlagen</div>
                    <ToggleRow
                      checked={attachInvoicePdfs}
                      onChange={setAttachInvoicePdfs}
                      label={`Factuur-PDF's meesturen (${selectedIds.size})`}
                      testId="composer-attach-invoice-pdfs"
                    />
                    <ToggleRow
                      checked={attachOutstandingOverview}
                      onChange={setAttachOutstandingOverview}
                      label="Openstaande-postenoverzicht (volgende sprint)"
                      disabled
                      testId="composer-attach-outstanding-overview"
                    />
                  </div>
                  {/* Sprint 18 Feb 2026 — Stripe betaallink management */}
                  {selectedIds.size === 1 && (
                    <div className="pt-2 border-t border-slate-100" data-testid="composer-stripe-block">
                      <div className="text-xs font-semibold uppercase text-slate-500 mb-2 flex items-center gap-1">
                        <CreditCard className="w-3 h-3" /> Online betaallink
                      </div>
                      {stripeSession ? (
                        <div className="space-y-2">
                          <div className="flex items-center gap-1 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded p-2">
                            <CheckCircle2 className="w-3 h-3" />
                            <span>Actieve Stripe-link — verschijnt als &quot;Betaal direct&quot; knop in mail</span>
                          </div>
                          <div className="flex gap-1">
                            <button
                              type="button"
                              onClick={() => window.open(stripeSession.stripe_payment_url || stripeSession.url, "_blank")}
                              className="text-[11px] flex-1 px-2 py-1 border border-slate-200 hover:bg-slate-100 rounded flex items-center justify-center gap-1"
                              data-testid="composer-stripe-open"
                            >
                              <ExternalLink className="w-3 h-3" /> Open Stripe
                            </button>
                            <button
                              type="button"
                              onClick={expireStripeLink}
                              className="text-[11px] px-2 py-1 border border-red-200 text-red-700 hover:bg-red-50 rounded"
                              data-testid="composer-stripe-expire"
                            >
                              Intrekken
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="text-[11px] text-slate-500">
                            Geen actieve link. Maak er een aan om de &quot;Betaal direct&quot; knop in de mail te tonen.
                          </div>
                          <Button
                            variant="outline"
                            onClick={createStripeLink}
                            disabled={stripeLoading}
                            className="w-full h-8 text-xs"
                            data-testid="composer-stripe-create"
                          >
                            {stripeLoading
                              ? <Loader2 className="w-3 h-3 animate-spin mr-1" />
                              : <CreditCard className="w-3 h-3 mr-1" />}
                            Betaallink aanmaken
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                  {/* Enterprise Composer sprint — Bundle Stripe checkout voor meerdere facturen */}
                  {selectedIds.size > 1 && (
                    <div className="pt-2 border-t border-slate-100" data-testid="composer-bundle-stripe-block">
                      <div className="text-xs font-semibold uppercase text-slate-500 mb-2 flex items-center gap-1">
                        <CreditCard className="w-3 h-3" /> Eén betaallink voor {selectedIds.size} facturen
                      </div>
                      {bundleSession ? (
                        <div className="space-y-2">
                          <div className="flex items-center gap-1 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded p-2">
                            <CheckCircle2 className="w-3 h-3" />
                            <span>Gebundelde betaallink actief · totaal {(bundleSession.currency || "EUR")} {Number(bundleSession.total_charge || bundleSession.total_amount || 0).toFixed(2)}</span>
                          </div>
                          <div className="text-[11px] text-slate-600">
                            Bij betaling wordt het bedrag automatisch verdeeld over alle {selectedIds.size} facturen via de <strong>InvoiceStatusService</strong> (single source of truth).
                          </div>
                          <button
                            type="button"
                            onClick={() => window.open(bundleSession.stripe_payment_url || bundleSession.url, "_blank")}
                            className="w-full text-[11px] px-2 py-1 border border-slate-200 hover:bg-slate-100 rounded flex items-center justify-center gap-1"
                            data-testid="composer-bundle-stripe-open"
                          >
                            <ExternalLink className="w-3 h-3" /> Open Stripe
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="text-[11px] text-slate-500">
                            Bundel {selectedIds.size} openstaande facturen in één Stripe-betaling. Bij succes worden alle facturen automatisch afgeletterd.
                          </div>
                          <Button
                            variant="outline"
                            onClick={createBundleStripeLink}
                            disabled={bundleLoading}
                            className="w-full h-8 text-xs"
                            data-testid="composer-bundle-stripe-create"
                          >
                            {bundleLoading
                              ? <Loader2 className="w-3 h-3 animate-spin mr-1" />
                              : <CreditCard className="w-3 h-3 mr-1" />}
                            Gebundelde betaallink aanmaken
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="pt-2 border-t border-slate-100">
                    <div className="text-xs font-semibold uppercase text-slate-500 mb-2">Betaalinformatie</div>
                    <ToggleRow checked={showBank} onChange={setShowBank} label="Bankgegevens tonen" testId="composer-show-bank" />
                    <ToggleRow checked={showPaymentReference} onChange={setShowPaymentReference} label="Betalingskenmerk tonen" testId="composer-show-reference" />
                    <ToggleRow checked={showPaymentLink} onChange={setShowPaymentLink} label="Betaallink tonen" testId="composer-show-link" />
                    <ToggleRow checked={showPaymentInstructions} onChange={setShowPaymentInstructions} label="Betaalinstructie tonen" testId="composer-show-instructions" />
                  </div>
                </div>
              </div>

              {/* ── Midden: subject + body + recipients ───────────────── */}
              <div className="lg:col-span-4 flex flex-col gap-3 min-h-0">
                <SectionTitle>Ontvangers</SectionTitle>
                <div className="space-y-2 border border-slate-200 rounded p-3">
                  <div>
                    <Label className="text-xs flex items-center gap-1">
                      Aan <span className="text-red-500">*</span>
                      {toList.length > 0 && (
                        <span className="text-[10px] text-emerald-700 flex items-center gap-1 ml-2">
                          <CheckCircle2 className="w-3 h-3" /> {toList.length} adres{toList.length > 1 ? "sen" : ""}
                        </span>
                      )}
                    </Label>
                    <Input
                      value={toInput}
                      onChange={(e) => setToInput(e.target.value)}
                      placeholder="klant@voorbeeld.nl"
                      className="mt-1"
                      data-testid="composer-to-input"
                    />
                    <div className="text-[10px] text-slate-500 mt-0.5">
                      Komma of puntkomma om meerdere adressen te scheiden.
                    </div>
                  </div>
                  {!showCcBcc ? (
                    <button
                      type="button"
                      onClick={() => setShowCcBcc(true)}
                      className="text-[11px] text-slate-600 hover:text-slate-900 underline"
                      data-testid="composer-show-ccbcc"
                    >
                      CC / BCC toevoegen
                    </button>
                  ) : (
                    <>
                      <div>
                        <Label className="text-xs">CC</Label>
                        <Input
                          value={ccInput}
                          onChange={(e) => setCcInput(e.target.value)}
                          placeholder="cc@voorbeeld.nl"
                          className="mt-1"
                          data-testid="composer-cc-input"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">BCC</Label>
                        <Input
                          value={bccInput}
                          onChange={(e) => setBccInput(e.target.value)}
                          placeholder="bcc@voorbeeld.nl (bv. eigen backoffice)"
                          className="mt-1"
                          data-testid="composer-bcc-input"
                        />
                      </div>
                    </>
                  )}
                </div>

                <SectionTitle>Onderwerp</SectionTitle>
                <Input
                  value={subject}
                  onChange={(e) => { setSubject(e.target.value); setBodyDirty(true); }}
                  data-testid="composer-subject-input"
                />

                <div className="flex items-center justify-between">
                  <SectionTitle>Bericht</SectionTitle>
                  {bodyDirty && (
                    <span className="text-[10px] text-amber-700 flex items-center gap-1">
                      <Info className="w-3 h-3" /> Aangepast
                    </span>
                  )}
                </div>
                {/* Quick-insert placeholders */}
                <div className="flex flex-wrap gap-1" data-testid="composer-placeholder-bar">
                  {PLACEHOLDERS.map((p) => (
                    <button
                      key={p.token}
                      type="button"
                      onClick={() => insertPlaceholder(p.token)}
                      title={`Invoegen: ${p.token} → ${p.label}`}
                      className="text-[10px] px-2 py-0.5 border border-slate-200 hover:bg-slate-100 rounded font-mono text-slate-700"
                      data-testid={`composer-insert-${p.token.replace(/[{}]/g, "")}`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                <Textarea
                  ref={bodyRef}
                  value={body}
                  onChange={(e) => { setBody(e.target.value); setBodyDirty(true); }}
                  rows={14}
                  className="flex-1 min-h-[280px] font-mono text-sm"
                  data-testid="composer-body-textarea"
                />
                <div className="text-[11px] text-slate-500">
                  Tekst tussen accolades wordt automatisch vervangen door klant/factuurgegevens. Kies een placeholder hierboven om deze op de cursorpositie in te voegen.
                </div>
              </div>

              {/* ── Rechts: live preview ──────────────────────────────── */}
              <div className="lg:col-span-4 flex flex-col gap-3 min-h-0">
                <div className="flex items-center justify-between">
                  <SectionTitle>Live preview</SectionTitle>
                  {previewLoading && (
                    <span className="text-[11px] text-slate-400 flex items-center gap-1">
                      <RefreshCw className="w-3 h-3 animate-spin" /> Bijwerken…
                    </span>
                  )}
                </div>
                {/* E-mail metadata banner */}
                <div className="border border-slate-200 rounded bg-slate-50 p-2 text-[11px] space-y-0.5" data-testid="composer-preview-meta">
                  <div className="flex gap-1"><span className="w-14 text-slate-500 uppercase">Aan</span><span className="flex-1 truncate font-medium">{toList.join(", ") || "—"}</span></div>
                  {ccList.length > 0 && (
                    <div className="flex gap-1"><span className="w-14 text-slate-500 uppercase">CC</span><span className="flex-1 truncate">{ccList.join(", ")}</span></div>
                  )}
                  {bccList.length > 0 && (
                    <div className="flex gap-1"><span className="w-14 text-slate-500 uppercase">BCC</span><span className="flex-1 truncate">{bccList.join(", ")}</span></div>
                  )}
                  <div className="flex gap-1"><span className="w-14 text-slate-500 uppercase">Onder-</span><span className="flex-1 truncate font-medium">{preview?.subject || subject}</span></div>
                </div>
                <div className="border border-slate-200 rounded overflow-hidden flex-1 min-h-[280px]" data-testid="composer-preview-frame">
                  {preview?.html ? (
                    <iframe
                      title="reminder-preview"
                      srcDoc={preview.html}
                      sandbox=""
                      className="w-full h-full min-h-[280px] bg-white"
                    />
                  ) : (
                    <div className="p-6 text-sm text-slate-400 italic">
                      Selecteer minimaal één factuur om de preview te tonen.
                    </div>
                  )}
                </div>
                {preview?.attachments_preview?.length > 0 && (
                  <div className="border border-slate-200 rounded p-3 bg-slate-50" data-testid="composer-attachments-preview">
                    <div className="text-xs font-semibold uppercase text-slate-600 mb-2 flex items-center gap-1">
                      <Paperclip className="w-3 h-3" />
                      Bijlagen ({preview.attachments_preview.length})
                      <span className="text-[10px] font-normal normal-case text-slate-500 ml-1">— exact wat er meegaat naar de klant</span>
                    </div>
                    <div className="space-y-1">
                      {preview.attachments_preview.map((a, i) => (
                        <div key={i} className="text-xs text-slate-800 flex items-center gap-2 bg-white border border-slate-200 rounded px-2 py-1" data-testid={`composer-attachment-item-${i}`}>
                          <CheckCircle2 className="w-3 h-3 text-emerald-600 flex-shrink-0" />
                          <FileText className="w-3 h-3 text-slate-400 flex-shrink-0" />
                          <span className="flex-1 truncate font-mono">{a.filename}</span>
                          {a.kind === "invoice_pdf" && (
                            <span className="text-[10px] text-slate-500">factuur-PDF</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Waarschuwingen + historie + acties (voetbalk) */}
            <div className="border-t border-slate-200 bg-slate-50 px-4 py-3 space-y-2 max-h-[35vh] overflow-y-auto">
              {combinedWarnings.length > 0 && (
                <div className="space-y-1" data-testid="composer-warnings">
                  {combinedWarnings.map((w, i) => (
                    <Alert key={i} className="border-amber-300 bg-amber-50 py-1.5">
                      <AlertTriangle className="h-4 w-4 text-amber-700" />
                      <AlertDescription className="text-xs text-amber-900">
                        {w}
                      </AlertDescription>
                    </Alert>
                  ))}
                </div>
              )}

              {history.length > 0 && (
                <div>
                  <button
                    type="button"
                    onClick={() => setShowHistory((v) => !v)}
                    className="text-xs font-semibold uppercase text-slate-600 mb-1 flex items-center gap-1 hover:text-slate-900"
                    data-testid="composer-toggle-history"
                  >
                    <History className="w-3 h-3" /> Historie ({history.length})
                    {showHistory ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  </button>
                  {showHistory && (
                    <div className="max-h-60 overflow-y-auto text-xs bg-white border border-slate-200 rounded" data-testid="composer-history">
                      {history.map((h) => {
                        const dt = (h.sent_at || h.created_at || "").slice(0, 16).replace("T", " ");
                        return (
                          <div key={h.id} className="border-b border-slate-100 last:border-b-0 px-3 py-2" data-testid={`composer-history-row-${h.id}`}>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-slate-800 font-medium">{dt}</span>
                              <Badge variant="outline" className="text-[10px]">{h.reminder_type || h.stage_key || "?"}</Badge>
                              {h.language && (
                                <Badge variant="outline" className="text-[10px] uppercase">{h.language}</Badge>
                              )}
                              <StatusPill status={h.status} />
                              {h.stripe_paid && (
                                <Badge className="text-[10px] bg-emerald-100 text-emerald-800 border-emerald-200">
                                  <CreditCard className="w-3 h-3 mr-0.5" /> Betaald via Stripe
                                </Badge>
                              )}
                            </div>
                            <div className="mt-1 text-slate-600 truncate">
                              <span className="text-slate-500">Onderwerp: </span>
                              {h.subject || "—"}
                            </div>
                            <div className="mt-0.5 flex items-center gap-3 text-[11px] text-slate-500 flex-wrap">
                              {h.sent_to && (
                                <span><span className="text-slate-400">Aan:</span> {h.sent_to}</span>
                              )}
                              {h.sent_by_email && (
                                <span><span className="text-slate-400">Door:</span> {h.sent_by_email}</span>
                              )}
                              {(h.provider_message_id || h.resend_message_id || h.message_id) && (
                                <span className="font-mono">
                                  <span className="text-slate-400">Resend:</span> {(h.provider_message_id || h.resend_message_id || h.message_id).slice(0, 12)}…
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between pt-1 gap-2 flex-wrap">
                <div className="text-[11px] text-slate-500 flex items-center gap-2">
                  <Info className="w-3 h-3" />
                  <span>Testmail wordt naar <span className="font-mono">{currentUserEmail || "uw account"}</span> gestuurd; geen historie geschreven.</span>
                </div>
                <div className="flex items-center gap-2 ml-auto">
                  <Button
                    variant="outline"
                    onClick={() => onOpenChange(false)}
                    disabled={sending}
                    data-testid="composer-cancel"
                  >
                    <X className="w-4 h-4 mr-1" /> Annuleren
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => submitSend(true)}
                    disabled={sending || selectedIds.size === 0}
                    data-testid="composer-testmail"
                  >
                    <Beaker className="w-4 h-4 mr-1" /> Testmail naar mezelf
                  </Button>
                  <Button
                    onClick={() => setConfirmOpen(true)}
                    disabled={sending || sendBlocked}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="composer-send"
                    title="Sneltoets: Ctrl+Enter"
                  >
                    {sending ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-1" />
                    ) : (
                      <Send className="w-4 h-4 mr-1" />
                    )}
                    <span className="hidden lg:inline">Nu verzenden</span>
                    <span className="lg:hidden">Verzend</span>
                    {toList[0] && (
                      <span className="hidden xl:inline ml-1 opacity-80">
                        → {toList[0]}
                      </span>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>

    {/* Bevestigingsdialog voor daadwerkelijk verzenden */}
    <SendConfirmDialog
      open={confirmOpen}
      onOpenChange={setConfirmOpen}
      onConfirm={() => submitSend(false)}
      to={toList}
      cc={ccList}
      bcc={bccList}
      subject={preview?.subject || subject}
      invoiceCount={selectedIds.size}
      totalDue={selectedTotalDue}
      currency={currency}
      attachmentCount={preview?.attachments_preview?.length || 0}
      sending={sending}
      remindersSentToday={remindersSentToday}
    />

    {/* Verzendbevestiging — professioneel resultaatvenster (P0.7) */}
    <SendResultDialog
      open={!!sendResult}
      result={sendResult}
      stripePayUrl={stripeSession?.stripe_payment_url || stripeSession?.url}
      onClose={() => {
        const wasSent = sendResult?.status === "sent";
        setSendResult(null);
        if (wasSent) onOpenChange(false);
      }}
    />
    </>
  );
}

// ─── Sub-components ──────────────────────────────────────────────

function SendConfirmDialog({
  open, onOpenChange, onConfirm, to, cc, bcc, subject,
  invoiceCount, totalDue, currency, attachmentCount, sending,
  remindersSentToday,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md rounded-md"
        data-testid="composer-send-confirm"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0A192F]">
            <Send className="w-5 h-5 text-emerald-600" />
            Bevestig verzenden
          </DialogTitle>
          <DialogDescription className="sr-only">
            Controleer ontvangers, onderwerp en bijlagen voor u de herinnering definitief verzendt.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-slate-600">
            U staat op het punt een echte betalingsherinnering te versturen. Controleer de gegevens hieronder.
          </p>
          {remindersSentToday > 0 && (
            <Alert className="border-amber-300 bg-amber-50 py-2">
              <AlertTriangle className="h-4 w-4 text-amber-700" />
              <AlertDescription className="text-xs text-amber-900">
                Deze klant heeft vandaag al {remindersSentToday} herinnering(en) ontvangen.
              </AlertDescription>
            </Alert>
          )}
          <div className="border border-slate-200 rounded p-3 space-y-1.5 bg-slate-50">
            <ConfirmRow label="Aan" value={to.join(", ") || "(leeg)"} highlight />
            {cc.length > 0 && <ConfirmRow label="CC" value={cc.join(", ")} />}
            {bcc.length > 0 && <ConfirmRow label="BCC" value={bcc.join(", ")} />}
            <ConfirmRow label="Onderwerp" value={subject || "(leeg)"} />
            <ConfirmRow label="Facturen" value={`${invoiceCount} stuk(s) — ${fmtMoney(totalDue, currency)} openstaand`} />
            <ConfirmRow label="Bijlagen" value={`${attachmentCount} PDF-bestand${attachmentCount === 1 ? "" : "en"}`} />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={sending}
            data-testid="composer-send-confirm-cancel"
          >
            <X className="w-4 h-4 mr-1" /> Nog even nakijken
          </Button>
          <Button
            onClick={onConfirm}
            disabled={sending}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="composer-send-confirm-ok"
          >
            {sending
              ? <Loader2 className="w-4 h-4 animate-spin mr-1" />
              : <Zap className="w-4 h-4 mr-1" />}
            Definitief verzenden
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConfirmRow({ label, value, highlight }) {
  return (
    <div className="flex text-xs gap-2">
      <span className="w-20 text-slate-500 uppercase text-[10px]">{label}</span>
      <span className={`flex-1 break-all ${highlight ? "font-semibold text-slate-900" : "text-slate-700"}`}>
        {value}
      </span>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 border-b border-slate-200 pb-1">
      {children}
    </div>
  );
}

function ToggleRow({ checked, onChange, label, disabled, testId }) {
  return (
    <label
      className={`flex items-center gap-2 text-sm py-0.5 cursor-pointer ${disabled ? "opacity-50" : ""}`}
    >
      <Checkbox
        checked={checked}
        onCheckedChange={(v) => !disabled && onChange(Boolean(v))}
        disabled={disabled}
        data-testid={testId}
      />
      <span>{label}</span>
    </label>
  );
}

function FinancialStatusBadge({ status }) {
  const meta = {
    concept: { label: "CONCEPT", cls: "bg-slate-100 text-slate-700 border-slate-300" },
    open: { label: "OPEN", cls: "bg-blue-100 text-blue-800 border-blue-300" },
    verzonden: { label: "VERZONDEN", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
    deels_betaald: { label: "DEELS BETAALD", cls: "bg-amber-100 text-amber-800 border-amber-300" },
    betaald: { label: "BETAALD", cls: "bg-green-100 text-green-800 border-green-300" },
    geannuleerd: { label: "GEANNULEERD", cls: "bg-red-100 text-red-800 border-red-300" },
  }[status] || { label: (status || "?").toUpperCase(), cls: "bg-gray-100 text-gray-700 border-gray-300" };
  return (
    <Badge variant="outline" className={`text-[10px] ${meta.cls}`}>{meta.label}</Badge>
  );
}

function StatusPill({ status }) {
  const cls = status === "sent"
    ? "bg-emerald-100 text-emerald-800"
    : status === "failed"
    ? "bg-red-100 text-red-800"
    : "bg-slate-100 text-slate-700";
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded ${cls}`}>{status || "?"}</span>
  );
}


// ─── Verzendbevestiging (P0.7) ───────────────────────────────────
function SendResultDialog({ open, result, stripePayUrl, onClose }) {
  if (!result) return null;
  const isSuccess = result.status === "sent";
  const isSkipped = result.status === "skipped_no_key";
  const sentAt = result.sent_at
    ? new Date(result.sent_at).toLocaleString("nl-NL", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      })
    : "—";

  const copy = (val) => {
    if (!val) return;
    navigator.clipboard.writeText(val).then(
      () => toast.success("Gekopieerd"),
      () => toast.error("Kopieren mislukt"),
    );
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="sm:max-w-lg rounded-md"
        data-testid="composer-send-result"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#0A192F]">
            {isSuccess ? (
              <CheckCircle2 className="w-6 h-6 text-emerald-600" />
            ) : isSkipped ? (
              <Info className="w-6 h-6 text-amber-600" />
            ) : (
              <AlertTriangle className="w-6 h-6 text-red-600" />
            )}
            {isSuccess ? "Herinnering succesvol verzonden" : isSkipped ? "Verzenden overgeslagen" : "Verzenden mislukt"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Resultaatoverzicht van de verzonden betalingsherinnering.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          {isSkipped && (
            <Alert className="border-amber-300 bg-amber-50 py-2">
              <AlertTriangle className="h-4 w-4 text-amber-700" />
              <AlertDescription className="text-xs text-amber-900">
                {result.message || "Resend API-sleutel ontbreekt op de server (preview omgeving)."}
              </AlertDescription>
            </Alert>
          )}
          <div className="border border-slate-200 rounded p-3 space-y-2 bg-slate-50" data-testid="composer-send-result-details">
            <ResultRow label="Aan" value={result.to || "—"} highlight />
            {result.cc?.length > 0 && <ResultRow label="CC" value={result.cc.join(", ")} />}
            {result.bcc?.length > 0 && <ResultRow label="BCC" value={result.bcc.join(", ")} />}
            <ResultRow label="Onderwerp" value={result.subject || "—"} />
            <ResultRow
              label="Facturen"
              value={
                result.invoice_count
                  ? `${result.invoice_count} — ${(result.invoice_numbers || []).join(", ")}`
                  : "—"
              }
            />
            <ResultRow
              label="Openstaand"
              value={
                result.total_due !== undefined && result.total_due !== null
                  ? `${result.currency || "EUR"} ${Number(result.total_due).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                  : "—"
              }
            />
            <ResultRow
              label="Bijlagen"
              value={
                result.attachment_count
                  ? `${result.attachment_count} — ${(result.attachment_filenames || []).slice(0, 2).join(", ")}${result.attachment_filenames?.length > 2 ? "…" : ""}`
                  : "0"
              }
            />
            {(result.provider_message_id || result.provider_id) && (
              <ResultRow
                label="Resend ID"
                value={
                  <span className="flex items-center gap-1">
                    <span className="font-mono">{result.provider_message_id || result.provider_id}</span>
                    <button
                      type="button"
                      onClick={() => copy(result.provider_message_id || result.provider_id)}
                      className="text-slate-500 hover:text-slate-800"
                      title="Kopieer"
                      data-testid="composer-copy-resend-id"
                    >
                      <Copy className="w-3 h-3" />
                    </button>
                  </span>
                }
              />
            )}
            {stripePayUrl && (
              <ResultRow
                label="Stripe-link"
                value={
                  <a
                    href={stripePayUrl} target="_blank" rel="noopener noreferrer"
                    className="text-blue-600 hover:underline font-mono text-[11px]"
                    data-testid="composer-result-stripe-link"
                  >
                    {stripePayUrl.substring(0, 40)}…
                  </a>
                }
              />
            )}
            <ResultRow label="Verzendtijd" value={sentAt} />
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={onClose}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="composer-send-result-close"
          >
            <CheckCircle2 className="w-4 h-4 mr-1" /> Sluiten
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResultRow({ label, value, highlight }) {
  return (
    <div className="flex text-xs gap-2 items-start">
      <span className="w-24 text-slate-500 uppercase text-[10px] flex-shrink-0 pt-0.5">{label}</span>
      <span className={`flex-1 break-all ${highlight ? "font-semibold text-slate-900" : "text-slate-700"}`}>
        {value}
      </span>
    </div>
  );
}

